import { useRef } from "react";
import { Camera, Images } from "lucide-react";

/**
 * Camera/gallery capture for the frosted-cake photo.
 *
 * Two skins: `driver` for the froster delivery shell (driver-* CSS) and
 * `default` for the shared task layout. The upload itself belongs to the page —
 * this component owns nothing but its two hidden file inputs, and hands the
 * chosen file straight to `onFileSelected`.
 */
export default function FrosterPhotoCapture({
  previewSrc,
  hasSaved,
  uploading,
  disabled,
  inputKey,
  onFileSelected,
  variant = "driver",
}) {
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const onChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || disabled || uploading) return;
    await onFileSelected(file);
  };

  const cameraBtn =
    variant === "driver" ? (
      <button
        type="button"
        disabled={disabled || uploading}
        onClick={() => cameraRef.current?.click()}
        className="driver-btn driver-btn-dark"
        style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
      >
        <Camera size={20} aria-hidden />
        {uploading ? "…" : hasSaved || previewSrc ? "Retake" : "Camera"}
      </button>
    ) : (
      <button
        type="button"
        disabled={disabled || uploading}
        onClick={() => cameraRef.current?.click()}
        className="flex items-center justify-center gap-2 rounded-xl bg-[#1a1a1a] px-4 py-3.5 text-sm font-bold text-white disabled:opacity-50"
      >
        <Camera size={18} aria-hidden />
        {uploading ? "…" : hasSaved || previewSrc ? "Retake" : "Camera"}
      </button>
    );

  const galleryBtn =
    variant === "driver" ? (
      <button
        type="button"
        disabled={disabled || uploading}
        onClick={() => galleryRef.current?.click()}
        className="driver-btn driver-btn-teal"
        style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
      >
        <Images size={20} aria-hidden />
        Gallery
      </button>
    ) : (
      <button
        type="button"
        disabled={disabled || uploading}
        onClick={() => galleryRef.current?.click()}
        className="flex items-center justify-center gap-2 rounded-xl bg-teal-700 px-4 py-3.5 text-sm font-bold text-white disabled:opacity-50"
      >
        <Images size={18} aria-hidden />
        Gallery
      </button>
    );

  const inputs = (
    <div key={inputKey}>
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        disabled={disabled || uploading}
        onChange={onChange}
        style={{ display: "none" }}
        aria-hidden
      />
      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        disabled={disabled || uploading}
        onChange={onChange}
        style={{ display: "none" }}
        aria-hidden
      />
    </div>
  );

  const previewBlock =
    hasSaved || previewSrc ? (
      <div style={{ textAlign: "center", marginBottom: 12 }}>
        <div
          className={
            variant === "driver"
              ? "driver-text-sm driver-text-green driver-font-medium driver-mb-3"
              : "mb-2 text-sm font-semibold text-green-700"
          }
        >
          {uploading ? "Uploading photo…" : hasSaved ? "✓ Frosted photo saved" : "Saving photo…"}
        </div>
        {previewSrc ? (
          <img
            src={previewSrc}
            alt="Frosted cake"
            className={variant === "driver" ? "driver-door-thumb" : "mx-auto max-h-56 w-full rounded-2xl border border-green-200 object-cover"}
            loading="lazy"
            decoding="async"
          />
        ) : null}
      </div>
    ) : null;

  if (variant === "driver") {
    return (
      <div className="driver-card" style={{ borderColor: "#15803D", background: "#F0FDF4" }}>
        <div className="driver-label">Frosted cake photo</div>
        <p className="driver-call-hint" style={{ marginBottom: 8 }}>
          Take a photo of the frosted cake. Customer gets a compare link when you tap Done.
        </p>
        {previewBlock}
        {inputs}
        <div className="driver-grid-2">
          {cameraBtn}
          {galleryBtn}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-3xl border-2 border-green-300 bg-green-50 p-3">
      <p className="mb-1 text-base font-bold text-[#166534]">Frosted cake photo</p>
      <p className="mb-3 text-sm text-[#166534]/80">Take a photo of the frosted cake before marking done.</p>
      {previewBlock}
      {inputs}
      <div className="grid grid-cols-2 gap-2.5">
        {cameraBtn}
        {galleryBtn}
      </div>
    </div>
  );
}
